import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { KTIcon } from '../../../../../_theme/helpers/components/KTIcon';
import { IOption } from '../../../core/_models';
import { IProjectTeam } from '../../core/_models';
import CustomFormSelect from '../../../apps/components/CustomFormSelect';
import CustomFormSearchSelect from '../../../apps/components/CustomFormSearchSelect';
import CustomInputPreview from '../../../apps/components/CustomInputPreview';
import SimpleLoader from '../../../apps/components/SimpleLoader';
import DeleteConfirmationModal from '../../../apps/components/DeleteConfirmationModal';
import { useSnackbar } from '../../../../../_theme/context/SnackbarContext';
import { addOrUpdateProjectTeam, deleteProjectTeam, getTeamMembersList } from '../../core/project/_request';
import { useTeamMembersMasterDataQuery } from '../../core/_queries';
import DynamicTable, { ColumnDef } from '../../../../../_theme/widgets/tables/DynamicTable';
import { LogError } from '../../../../../utils/GlobalLogging';
import { isAbortedError, PROJECT_MANAGER_ROLE, PROJECT_OWNER_ROLE, PROJECT_LEAD_ROLE } from '../../../../../utils/global';
import { getApiErrorMessage } from "../../../../../utils/utils";

export const ACCESS_OPTIONS: IOption[] = [
  { label: 'Edit', value: 'edit' },
  { label: 'View', value: 'view' },
];


/** Roles that cannot have their role changed (owner only). */
export const PROTECTED_ROLES = [PROJECT_OWNER_ROLE];

/** Roles that may be assigned to only one team member. */
export const UNIQUE_ROLES: string[] = [
  PROJECT_OWNER_ROLE,
  PROJECT_MANAGER_ROLE,
  PROJECT_LEAD_ROLE,
];

export const defaultTeamMemberFormData: IProjectTeam = {
  user_id: 0,
  role_id: '',
  project_rights: 'edit',
  new: true,
};

type Props = {
  show: boolean;
  onHide: () => void;
  project_id?: string;
  disableFormFields?: boolean;
};

type ModeState = {
  isOpen: boolean;
  mode: 'create' | 'edit' | 'delete' | null;
  item: IProjectTeam | null;
  index: number | null;
  originalRoleId: string;
};

const RESET_MODE_STATE: ModeState = {
  isOpen: false,
  mode: null,
  item: null,
  index: null,
  originalRoleId: '',
};

const ManageTeamMembersModal: FC<Props> = ({
  show,
  onHide,
  project_id,
  disableFormFields = false
}) => {
  const { users, projectRoles, isLoading } =
    useTeamMembersMasterDataQuery();
  const { showSnackbar } = useSnackbar();
  const [members, setMembers] = useState<IProjectTeam[]>([]);
  const [processing, setProcessing] = useState(false);
  const [modeState, setModeState] = useState<ModeState>(RESET_MODE_STATE);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    setModeState(RESET_MODE_STATE);
  }, [show]);

  const fetchTeamMembers = useCallback(async () => {
    if (!project_id) {
      return;
    }

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setProcessing(true);
      const response = await getTeamMembersList(
        Number(project_id),
        controller.signal
      );

      if (controller.signal.aborted) return;

      if (response.status === 'ok') {
        setMembers(response.data?.list ?? []);
      } else {
        showSnackbar(response.message, 'danger');
      }
    } catch (err) {
      if (isAbortedError(err)) return;
      showSnackbar(getApiErrorMessage(err, 'Failed to fetch team members'), 'danger');
      LogError("Failed to fetch team members", err, "ManageTeamMembersModal");
    } finally {
      if (!controller.signal.aborted) {
        setProcessing(false);
      }
    }
  }, [project_id, showSnackbar]);

  useEffect(() => {
    fetchTeamMembers();
    return () => {
      abortControllerRef.current?.abort();
    };
  }, [fetchTeamMembers]);

  const getRoleName = useCallback(
    (roleId: string) =>
      projectRoles.find((role) => role.data_key === roleId)?.display_name || roleId || '-',
    [projectRoles]
  );

  const getUserDetails = useCallback(
    (row: IProjectTeam) => {
      const user = users.find((item) => String(item.user_id) === String(row.user_id));
      return {
        fullname: row.fullname || user?.fullname || '-',
        email: row.email || user?.email || '-',
      };
    },
    [users]
  );

  const takenUniqueRoles = useMemo(() => {
    const taken = new Set<string>();
    members.forEach((member) => {
      if (!member.new && UNIQUE_ROLES.includes(member.role_id)) {
        taken.add(member.role_id);
      }
    });
    return taken;
  }, [members]);

  const getRoleOptions = useCallback(
    (row: IProjectTeam) =>
      projectRoles
        .filter((role) => {
          if (row.role_id === role.data_key) {
            return true;
          }
          return !takenUniqueRoles.has(role.data_key);
        })
        .map((role) => ({
          label: role.display_name,
          value: role.data_key,
        })),
    [projectRoles, takenUniqueRoles]
  );

  const hasUnsavedEdit =
    modeState.isOpen && (modeState.mode === 'create' || modeState.mode === 'edit');

  const isEditingRow = useCallback(
    (index: number) =>
      hasUnsavedEdit && modeState.index === index,
    [hasUnsavedEdit, modeState.index]
  );

  const draftRow =
    hasUnsavedEdit ? modeState.item : null;

  const managerCount = useMemo(
    () => members.filter((member) => member.role_id === PROJECT_MANAGER_ROLE && !member.new).length,
    [members]
  );

  const canEditOrDeleteMember = useCallback(
    (row: IProjectTeam) => {
      if (row.role_id === PROJECT_OWNER_ROLE) {
        return false;
      }else if (row.role_id === PROJECT_MANAGER_ROLE) {
        return false;
      }
      return true;
    },
    [managerCount]
  );

  /** Owner always locked; sole Project Manager cannot change role. */
  const isRoleLocked = useCallback(
    (row: IProjectTeam) => {
      if (row.role_id === PROJECT_OWNER_ROLE) {
        return true;
      }
      if (row.role_id === PROJECT_MANAGER_ROLE && managerCount <= 1) {
        return true;
      }
      return false;
    },
    [managerCount]
  );

  const addRow = useCallback(() => {
    if (disableFormFields) {
      return;
    }
    if (hasUnsavedEdit) {
      showSnackbar('Please save or cancel the current row first', 'danger');
      return;
    }

    const newRow = { ...defaultTeamMemberFormData };
    setMembers((prev) => [...prev, newRow]);
    setModeState({
      isOpen: true,
      mode: 'create',
      item: newRow,
      index: members.length,
      originalRoleId: '',
    });
  }, [disableFormFields, hasUnsavedEdit, members.length, showSnackbar]);

  const startEdit = useCallback(
    (index: number) => {
      if (disableFormFields) {
        return;
      }
      if (hasUnsavedEdit) {
        showSnackbar('Please save or cancel the current row first', 'danger');
        return;
      }

      const row = members[index];
      if (!row) {
        return;
      }

      setModeState({
        isOpen: true,
        mode: 'edit',
        item: { ...row },
        index,
        originalRoleId: row.role_id,
      });
    },
    [disableFormFields, hasUnsavedEdit, members, showSnackbar]
  );

  const cancelEdit = useCallback(() => {
    if (!hasUnsavedEdit || !modeState.item || modeState.index === null) {
      setModeState(RESET_MODE_STATE);
      return;
    }

    if (modeState.mode === 'create') {
      setMembers((prev) => prev.filter((_, index) => index !== modeState.index));
    }

    setModeState(RESET_MODE_STATE);
  }, [hasUnsavedEdit, modeState]);

  const handleDraftChange = useCallback(
    (field: keyof IProjectTeam, value: string | number) => {
      setModeState((prev) => {
        if (!prev.item || (prev.mode !== 'create' && prev.mode !== 'edit')) {
          return prev;
        }
        return {
          ...prev,
          item: { ...prev.item, [field]: value },
        };
      });
    },
    []
  );

  const saveRow = useCallback(async () => {
    if (!hasUnsavedEdit || modeState.index === null || !modeState.item) {
      return;
    }

    const draftRow = modeState.item;
    const editingIndex = modeState.index;
    const { originalRoleId } = modeState;

    if (!project_id) {
      showSnackbar('Project id is required', 'danger');
      return;
    }

    if (!draftRow.user_id) {
      showSnackbar('User is required', 'danger');
      return;
    }
    if (!draftRow.role_id) {
      showSnackbar('Role is required', 'danger');
      return;
    }
    if (!draftRow.project_rights) {
      showSnackbar('Access is required', 'danger');
      return;
    }

    if (
      originalRoleId === PROJECT_MANAGER_ROLE &&
      managerCount <= 1 &&
      draftRow.role_id !== originalRoleId
    ) {
      showSnackbar(
        'Cannot change role of the only Project Manager. Add another manager first.',
        'danger'
      );
      return;
    }

    if (
      originalRoleId === PROJECT_OWNER_ROLE &&
      draftRow.role_id !== originalRoleId
    ) {
      showSnackbar('Project Owner role cannot be changed', 'danger');
      return;
    }

    if (
      UNIQUE_ROLES.includes(draftRow.role_id) &&
      members.some(
        (member, index) =>
          index !== editingIndex &&
          member.role_id === draftRow.role_id &&
          !member.new
      )
    ) {
      showSnackbar(
        `${getRoleName(draftRow.role_id)} can only be assigned to one team member`,
        'danger'
      );
      return;
    }

    const duplicate = members.some(
      (member, index) =>
        index !== editingIndex &&
        Number(member.user_id) === Number(draftRow.user_id) &&
        member.role_id === draftRow.role_id
    );
    if (duplicate) {
      showSnackbar('User already has this role on the project', 'danger');
      return;
    }

    const selectedUser = users.find(
      (user) => String(user.user_id) === String(draftRow.user_id)
    );

    try {
      setProcessing(true);

      const payload = {
        project_id: Number(project_id),
        member_user_id: Number(draftRow.user_id),
        role_id: draftRow.role_id,
        project_rights: draftRow.project_rights,
        ...(!draftRow.new && originalRoleId ? { old_role_id: originalRoleId } : {}),
      };

      const response = await addOrUpdateProjectTeam(payload);
      if (response?.status !== 'ok') {
        showSnackbar(response?.message || 'Failed to save team member', 'danger');
        return;
      }

      const savedRow: IProjectTeam = {
        ...draftRow,
        fullname: selectedUser?.fullname || draftRow.fullname,
        email: selectedUser?.email || draftRow.email,
        new: false,
      };

      const updatedRows = members.map((row, index) =>
        index === editingIndex ? savedRow : row
      );
      setMembers(updatedRows);
      setModeState(RESET_MODE_STATE);
      showSnackbar(
        response.message ||
        (draftRow.new
          ? 'Team member added successfully'
          : 'Team member updated successfully'),
        'success'
      );
    } catch (error) {
      showSnackbar(getApiErrorMessage(error, 'Failed to save team member'), 'danger');
      LogError("Failed to save team member", error, "ManageTeamMembersModal");
    } finally {
      setProcessing(false);
    }
  }, [
    hasUnsavedEdit,
    modeState,
    project_id,
    members,
    users,
    managerCount,
    getRoleName,
    showSnackbar,
  ]);

  const requestDelete = useCallback(
    (index: number) => {
      if (hasUnsavedEdit) {
        showSnackbar('Please save or cancel the current row first', 'danger');
        return;
      }

      const member = members[index];
      if (!member) {
        return;
      }

      if (member.role_id === PROJECT_OWNER_ROLE) {
        showSnackbar('Project Owner cannot be deleted', 'danger');
        return;
      }

      if (member.role_id === PROJECT_MANAGER_ROLE && managerCount <= 1) {
        showSnackbar(
          'Cannot delete the only Project Manager. Add another manager first.',
          'danger'
        );
        return;
      }

      setModeState({
        isOpen: true,
        mode: 'delete',
        item: member,
        index,
        originalRoleId: '',
      });
    },
    [hasUnsavedEdit, showSnackbar, members, managerCount]
  );

  const confirmDelete = useCallback(async () => {
    if (!modeState.isOpen || modeState.mode !== 'delete' || modeState.index === null) {
      return;
    }

    const member = modeState.item ?? members[modeState.index];
    if (!member) {
      showSnackbar('Team member not found', 'danger');
      return;
    }

    try {
      setProcessing(true);

      if (!member.new && project_id) {
        const response = await deleteProjectTeam({
          delete_id: Number(member.user_id),
          project_id: Number(project_id),
          role_id: member.role_id,
        });

        if (response?.status !== 'ok') {
          showSnackbar(response?.message || 'Failed to delete team member', 'danger');
          return;
        }
        showSnackbar(response.message || 'Team member deleted successfully', 'success');
      } else {
        showSnackbar('Team member deleted successfully', 'success');
      }

      const updatedRows = members.filter((_, index) => index !== modeState.index);
      setMembers(updatedRows);
      setModeState(RESET_MODE_STATE);
    } catch (error) {
      showSnackbar(getApiErrorMessage(error, 'Failed to delete team member'), 'danger');
      LogError("Failed to delete team member", error, "ManageTeamMembersModal");
    } finally {
      setProcessing(false);
    }
  }, [modeState, members, project_id, showSnackbar]);

  const handleClose = useCallback(() => {
    if (hasUnsavedEdit) {
      showSnackbar('Please save or cancel the current row first', 'danger');
      return;
    }
    onHide();
  }, [hasUnsavedEdit, onHide, showSnackbar]);

  const userOptions = useMemo(() => {
    const disableMembers = members.map((member) => member.user_id);
    return users.filter((user) => !disableMembers.includes(user.user_id)).map((user) => ({
      label: user.fullname,
      value: user.user_id,
    }));
  }, [users, members]);

  const columns = useMemo(() => {
    const tableColumns: ColumnDef<IProjectTeam>[] = [
      {
        header: 'Name',
        renderCell: (row: IProjectTeam, index: number) => {
          const isEditing = isEditingRow(index);
          const activeRow = isEditing && draftRow ? draftRow : row;
          const userDetails = getUserDetails(activeRow);
          const isUserLocked = isEditing && !activeRow.new;

          if (!isEditing) {
            return userDetails.fullname;
          }

          if (isUserLocked) {
            return <CustomInputPreview value={userDetails.fullname} size='sm' />;
          }

          return (
            <CustomFormSearchSelect
              value={activeRow.user_id || null}
              disabled={disableFormFields}
              onChange={(value) =>
                handleDraftChange('user_id', value as string | number)
              }
              options={userOptions}
              placeholder="Select User"
              usePortal
              size='sm'
            />
          );
        },
      },
      {
        header: 'Email',
        renderCell: (row: IProjectTeam, index: number) => {
          const isEditing = isEditingRow(index);
          const activeRow = isEditing && draftRow ? draftRow : row;
          const userDetails = getUserDetails(activeRow);

          return isEditing
            ? <CustomInputPreview value={userDetails.email} size='sm' />
            : userDetails.email;
        },
      },
      {
        header: 'Role',
        renderCell: (row: IProjectTeam, index: number) => {
          const isEditing = isEditingRow(index);
          const activeRow = isEditing && draftRow ? draftRow : row;

          if (!isEditing) {
            return getRoleName(row.role_id);
          }

          return (
            <CustomFormSelect
              value={activeRow.role_id}
              onChange={(event) => handleDraftChange('role_id', event.target.value)}
              options={getRoleOptions(activeRow)}
              disabled={disableFormFields || isRoleLocked(row)}
              placeholder="Select Role"
              size='sm'
            />
          );
        },
      },
    ];

    if (!disableFormFields) {
      tableColumns.push({
        header: (
          <div className="text-center">
            <Button variant="light-primary" size="sm" onClick={addRow}>
              <KTIcon iconName="plus-square" className="fs-3 me-1 pe-0" />
              Add Member
            </Button>
          </div>
        ),
        renderCell: (row: IProjectTeam, index: number) => {
          const isEditing = isEditingRow(index);
          const allowEditOrDelete = canEditOrDeleteMember(row);

          if (isEditing) {
            return (
              <div className="text-center">
                <Button
                  variant="light-success"
                  size="sm"
                  className="me-2 p-2"
                  onClick={saveRow}
                  disabled={processing}
                  title="Save"
                >
                  {processing ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <KTIcon iconName="check" className="fs-3 pe-0" />
                  )}
                </Button>
                <Button
                  variant="light-warning"
                  size="sm"
                  className="p-2"
                  onClick={cancelEdit}
                  disabled={processing}
                  title="Cancel"
                >
                  <KTIcon iconName="cross" className="fs-3 pe-0" />
                </Button>
              </div>
            );
          }

          const deleteTitle =
            row.role_id === PROJECT_OWNER_ROLE
              ? 'Project Owner cannot be deleted'
              : row.role_id === PROJECT_MANAGER_ROLE && managerCount <= 1
                ? 'Cannot delete the only Project Manager'
                : 'Delete';

          return (
            <div className="text-center">
              <Button
                variant="light-info"
                size="sm"
                className="me-2 p-2"
                onClick={() => startEdit(index)}
                title="Edit"
                disabled={!allowEditOrDelete}
              >
                <KTIcon iconName="pencil" className="fs-3 pe-0" />
              </Button>
              <Button
                variant="light-danger"
                size="sm"
                className={`p-2 ${!allowEditOrDelete ? 'opacity-25' : ''}`}
                onClick={() => requestDelete(index)}
                title={deleteTitle}
                disabled={!allowEditOrDelete}
              >
                <KTIcon iconName="trash" className="fs-3 pe-0" />
              </Button>
            </div>
          );
        },
        width: '150px',
      });
    }

    return tableColumns;
  }, [
    isEditingRow,
    draftRow,
    disableFormFields,
    userOptions,
    processing,
    managerCount,
    addRow,
    cancelEdit,
    canEditOrDeleteMember,
    getRoleName,
    getRoleOptions,
    getUserDetails,
    handleDraftChange,
    isRoleLocked,
    requestDelete,
    saveRow,
    startEdit,
  ]);

  const showLoader = processing || isLoading;

  return (
    <>
      <Modal
        centered
        size="xl"
        show={show}
        onHide={handleClose}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header className="p-4 border-0">
          <Modal.Title>Manage Project Team</Modal.Title>
          <div
            className="btn btn-sm btn-icon btn-active-color-primary"
            onClick={handleClose}
            style={{ cursor: 'pointer' }}
          >
            <KTIcon className="fs-1" iconName="cross" />
          </div>
        </Modal.Header>

        <Modal.Body className="p-4 position-relative">
          {showLoader && <SimpleLoader showbackdrop={true} text="Saving..." />}

          <div className="table-responsive">
            <DynamicTable
              columns={columns}
              data={members}
              extraClassName="align-middle w-100 mb-0"
              emptyMessage="No team members added yet."
            />
          </div>
        </Modal.Body>

        <Modal.Footer className="p-4">
          <Button variant="secondary" onClick={handleClose} disabled={processing}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <DeleteConfirmationModal
        show={modeState.isOpen && modeState.mode === 'delete'}
        onClose={() => setModeState(RESET_MODE_STATE)}
        onConfirm={confirmDelete}
        deleteMessage="Are you sure you want to delete this member?"
        popupLoader={processing}
      />
    </>
  );
};

export default ManageTeamMembersModal;
